import { getApiFilters } from '~/shared/utils/apiFilters';
import { SelectOption } from '~/shared/utils/types';

import {
  apiFiltersParamKey,
  PROCESSED_FILTERS_PARAM_KEY,
} from '~/shared/utils/filterUrlParams';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  FilterStoredInURL,
  filterNonSpecificGroups,
  combineFilters,
} from './filterUtils';
import type { VolumeFilterPanelState } from './panelState';

/**
 * The pure URL-param transforms behind the volume filter panel. Extracted from
 * `useFilterUrlParams` so they can be characterized in tests — `URLSearchParams` is a Node
 * global, so this is the panel's entire observable effect on the app, testable without jsdom.
 */

export function setAutoscalingUrlParams(
  updatedParams: URLSearchParams,
  stateIds: string[],
  eligibilityIds: string[]
) {
  if (stateIds.length > 0) {
    updatedParams.set(AUTOSCALING_STATE_PARAM_KEY, stateIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
  }

  if (eligibilityIds.length > 0) {
    updatedParams.set(AUTOSCALING_ELIGIBILITY_PARAM_KEY, eligibilityIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_ELIGIBILITY_PARAM_KEY);
  }
}

/**
 * The api_filters list to write for a given panel state: whatever the URL already held that
 * this panel does not own, then the panel's own selections in `combineFilters`' fixed order.
 */
export function buildFinalApiFilters(
  existingFiltersFromUrl: FilterStoredInURL[],
  pending: VolumeFilterPanelState
): SelectOption[] {
  return combineFilters(filterNonSpecificGroups(existingFiltersFromUrl), pending);
}

export function writeFilterParams(
  updatedParams: URLSearchParams,
  finalApiFilters: SelectOption[],
  stateIds: string[],
  eligibilityIds: string[]
) {
  setAutoscalingUrlParams(updatedParams, stateIds, eligibilityIds);

  if (finalApiFilters.length > 0) {
    updatedParams.set(apiFiltersParamKey, JSON.stringify(finalApiFilters));
    const processedFilters = getApiFilters(finalApiFilters);
    updatedParams.set(PROCESSED_FILTERS_PARAM_KEY, JSON.stringify(processedFilters));
  } else {
    updatedParams.delete(apiFiltersParamKey);
    updatedParams.delete(PROCESSED_FILTERS_PARAM_KEY);
  }
}
